/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
/* eslint-disable multiline-ternary */
import React, { useState } from "react";
import * as TOML from "@ltd/j-toml";
import { Typography, Grid } from "@mui/material";
import { Button } from "components/haven-design/buttons";
import { validateQuestionByType } from "./form-builder/question-type-specs";
import {
  questionNamePartsOf,
  questionToQuestionString,
  questionTypeOf,
  validateQuestionDataKeys,
  findQuestionDataKeyWarnings,
} from "./form-builder/question-data-keys";
import {
  isValidQuestionIdentifier,
  QUESTION_IDENTIFIER_RULE,
  isValidContextValue,
  CONTEXT_VALUE_RULE,
  isValidSectionIdentifier,
  SECTION_IDENTIFIER_RULE,
} from "./form-builder/question-identifier-validation";

const keyToQuestionString = (questionName: string): string => questionToQuestionString(questionName.split("-"));

type GroupType = {
  name?: string;
  title?: string;
  description?: string;
  questions?: string[][];
}

const validateLayout = (layout: {group: GroupType[]}[]): string[] => {
  const errors:string[] = [];
  // @ts-ignore
  if ((Array.isArray(layout[0].group) && layout[0].group.length)) {
    layout[0].group.forEach((group: GroupType) => {
      if (group.name) {
        const sectionName = group.name.trim();
        if (!isValidSectionIdentifier(sectionName)) {
          errors.push(
            `[[layout.group]] ${group.name} should use ${SECTION_IDENTIFIER_RULE}`,
          );
        }
        if (!group.title) {
          errors.push(`[[layout.group]] ${group.name} should have a title`);
        }
        if (!group.description) {
          errors.push(`[[layout.group]] ${group.name} should have a description`);
        }
      } else {
        errors.push("every [[layout.group]] should have name= ");
      }
    });
  } else {
    errors.push("At least one [[layout.group]] has to be added");
  }
  return errors;
};

const validateWeightCommand = (weight: any, child = false): string[] => {
  const errors:string[] = [];
  if (!child) {
    if (!weight.command) return ["command is required for weights"];
    if (!weight.command.value) errors.push("command.value for weight is required");

    if (!["increment", "decrement"].includes(weight.command.operator)) {
      errors.push("command.operator for weight should be increment or decrement");
    }
  }
  return errors;
};
// eslint-disable-next-line func-style
function validateWeightCondition (condition: any): string[] {
  const errors:string[] = [];
  if (!condition.type) {
    errors.push("condition should have type");
  } else if (!["and", "or"].includes(condition.type)) {
    errors.push("condition.type should be either \"and\" or \"or\"");
  }
  if (condition.rule) {
    validateWeight(condition.rule, true).forEach((error) => errors.push(error));
  } else {
    errors.push("condition should have rule");
  }
  return errors;
}
// eslint-disable-next-line func-style
function validateWeight (weight: WeightType, child = false): string[] {
  let errors:string[] = [];

  errors = errors.concat(validateWeightCommand(weight, child));
  if (!weight.name) errors.push("weight should have question name. e.g name=[\"first_name\", \"applicant\"]");
  if (weight.operator) {
    if (!["equals", "not_equals", "greater_than", "less_than"].includes(weight.operator)) {
      errors.push("weight.operator should be one of \"equals\", \"not_equals\", \"greater_than\", \"less_than\"");
    }
  } else {
    errors.push("weight should have operator");
  }
  if (!weight.value) errors.push("weight should have value");
  const conditionErrors = (weight.conditions || [])
    .map(validateWeightCondition)
    .flat(1);
  errors = errors.concat(conditionErrors);
  return errors;
}

const validateWeights = (weights: WeightType[]): string[] =>
  weights.flatMap((weight) => validateWeight(weight));

type Questions = Record<string, string>

type WeightType = {
  name: string[];
  operator: "equals" | "greater_than" | "less_than";
  value: number;
  command: {
    operator: "increment" | "decrement";
    value: number;
  },
  conditions?: {
    type: "and" | "or";
    rule: WeightType[];
  }[];
}

const validateContent = (content:string | undefined, existingQuestions?: Questions):string[] => {
  try {
    const errors:string[] = [];
    const parsed = TOML.parse[0.5](content || "", "\n");
    // Callers that only need parse success omit existingQuestions.
    if (existingQuestions === undefined) return [];
    if (!(Array.isArray(parsed.question) && parsed.question.length)) {
      errors.push("At least one [[question]] has to be added");
    }
    const layoutErrors = validateLayout(parsed.layout as {group: GroupType[]}[]);
    layoutErrors.forEach((layoutError) => errors.push(layoutError));
    if (parsed.weights) {
      const weightsErrors = validateWeights(parsed.weights as WeightType[]);
      weightsErrors.forEach((weightsError) => errors.push(weightsError));
    }

    const questionsList = parsed.question as Record<string, any>[];
    // ensure all questions are defined in FormQuestions table
    questionsList.forEach((question) => {
      const questionNameParts = questionNamePartsOf(question);
      const [questionName, ...contextValues] = questionNameParts;
      const questionString = questionToQuestionString(questionNameParts);

      if (!questionName) {
        errors.push("every [[question]] should have name=[\"question_name\", \"context\"] set");
        return;
      }

      if (!isValidQuestionIdentifier(questionName)) {
        errors.push(
          `${questionString} has invalid question name "${questionName
          }". Use ${QUESTION_IDENTIFIER_RULE}.`,
        );
      }

      const invalidContextValues = contextValues
        .filter((contextValue: string) => !isValidContextValue(contextValue));
      if (invalidContextValues.length > 0) {
        errors.push(
          `${questionString} has invalid context values: ${invalidContextValues.join(", ")}. Use ${CONTEXT_VALUE_RULE}.`,
        );
      }

      const questionAlias = `${question.alias || ""}`.trim();
      if (questionAlias && !isValidQuestionIdentifier(questionAlias)) {
        errors.push(
          `${questionString} has invalid alias "${questionAlias
          }". Use ${QUESTION_IDENTIFIER_RULE}.`,
        );
      }

      const questionType = questionTypeOf(questionName, existingQuestions);
      if (questionType) {
        validateQuestionByType(question, questionType, questionString)
          .concat(validateQuestionDataKeys(question, questionType, questionString))
          .forEach((result) => errors.push(result));
      } else {
        errors.push(`${questionName} is not registered in FormQuestions`);
      }
      if (question.cond) {
        if (question.cond.type !== "includes") {
          errors.push(`${questionName} condition type ${question.cond.type} 
            is not supported`);
        }
        if (!question.cond.values || !Array.isArray(question.cond.values)) {
          errors.push(`${questionName} condition should have values list to be set`);
        } else if (!question.cond.values.length) {
          errors.push(`${questionName} condition values should have at least one value`);
        }
      }
    });

    const questionsToggle = questionsList.reduce((acc, { name }) => {
      const key = name.join("-");
      if (key in acc) {
        errors.push(`${keyToQuestionString(key)} is a duplicate key`);
      }
      return ({ ...acc, [key]: false });
    }, {});
    // = question.data?.options?
    const toggledChildren = questionsList.reduce((acc, question) => {
      let optionsChildren = [];
      if (question.data?.options) {
        if (Array.isArray(question.data?.options)) {
          optionsChildren = question.data?.options
            .reduce((optionAcc: any[], option: any) =>
              optionAcc.concat(option.children || []), []);
        } else {
          optionsChildren = question.data.options.children;
        }
      }
      const allChildren = (question.children || [])
        .concat(question.data?.children || [])
        .concat(optionsChildren || []);
      if (allChildren.length && !allChildren.every(Array.isArray)) {
        errors.push(`One of children for question ${question.name} is not correct`);
        return acc;
      }
      return allChildren.reduce((nestedAcc: any, name: any) => ({ ...nestedAcc, [name.join("-")]: true }), acc);
    }, questionsToggle);
    const parsedData = (parsed.layout as {group: any[]}[])[0]
      .group.reduce((acc: any, { questions }: any) =>
        questions.reduce((nestedAcc: any, name: string[]) => {
          const key = name.join("-");
          if (!(key in questionsToggle)) {
            errors.push(`${keyToQuestionString(key)} is in question group 
            but has no [[question]] definition`);
          }
          return ({ ...nestedAcc, [key]: true });
        }, acc), toggledChildren);
    questionsList
      .reduce((acc, { children }) => acc.concat(children || []), [])
      .forEach((childQuestionName: string[]) => {
        if (typeof questionsToggle[childQuestionName.join("-")] === "undefined") {
          errors.push(`["${childQuestionName.join("\", \"")}"] is not defined`);
        }
      });

    Object.entries(parsedData).forEach(([questionName, value]) => {
      if (!value) {
        errors.push(`${keyToQuestionString(questionName)} is not linked 
        to any question or group`);
      }
    });
    return errors;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log("err", err);
    return [String(err)];
  }
};

type Validation = { errors: string[], warnings: string[] };
type TOMLValidatorProps = {
  content?: string,
  existingQuestions: Record<string, string>,

  /** When set (e.g. visual builder), runs first so validation uses merged TOML from the builder. */
  resolveContentBeforeValidate?: () => Promise<string>,
}

const TOMLValidator: React.FC<TOMLValidatorProps> = ({
  content,
  existingQuestions,
  resolveContentBeforeValidate,
}) => {
  const [validation, setValidation] = useState<Validation | false>(false);
  const validate = async () => {
    const resolved = resolveContentBeforeValidate
      ? await resolveContentBeforeValidate()
      : (content ?? "");
    setValidation({
      errors: validateContent(resolved, existingQuestions),
      warnings: findQuestionDataKeyWarnings(resolved),
    });
  };
  const { errors, warnings } = validation || { errors: [], warnings: [] };
  const messages = errors.map((text) => ({ text, color: "text.primary" }))
    .concat(warnings.map((text) => ({ text: `Warning: ${text}`, color: "warning.main" })));
  return (<>
    <Button size="L" onClick={validate} type="button" color={errors.length > 0 ? "dangerous" : "primary"} >Validate</Button>
    {validation && errors.length === 0 && <Typography variant="body1">Valid</Typography>}
    <Grid container>
      <ul>
        {messages.map(({ text, color }, idx) => (
          <li key={`${idx}validationError`}><Typography variant="body1" color={color}>{text}</Typography></li>))}
      </ul>
    </Grid>
  </>);
};

export { validateContent };

export default TOMLValidator;
